import type { DataResult } from './data-result';
import { escapeHtml } from './html';
import { fullSkillDigest } from './skill-digest-display';
import { installCommandForTarget, renderSkillMd } from './skill-detail-panels';
import type { SkillDetail, SkillFeedbackResponse, UsageStatsResponse } from '../types/api';

async function tryCopyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

async function copyTextWithStatus(
  text: string,
  statusEl: HTMLElement | null,
  successMessage: string,
  failureMessage: string,
): Promise<void> {
  if (await tryCopyText(text)) {
    if (statusEl) statusEl.textContent = successMessage;
    return;
  }
  if (statusEl) statusEl.textContent = failureMessage;
}

export async function copyTextWithToast(
  text: string,
  successMessage: string = 'Copied',
  failureMessage: string = 'Copy failed. Select and copy manually.',
): Promise<void> {
  if (await tryCopyText(text)) {
    showToast(successMessage);
    return;
  }
  showToast(failureMessage, 'error');
}

export function bindSkillDetailCopyActions(skill: SkillDetail): void {
  const installTarget = document.getElementById('skill-install-target') as HTMLSelectElement | null;
  const installCommand = document.getElementById('skill-install-command');
  const installCopy = document.getElementById('skill-install-copy') as HTMLButtonElement | null;
  const installCopyStatus = document.getElementById('skill-install-copy-status');
  const directUseUrl = document.getElementById('skill-direct-use-url');
  const directUseCopy = document.getElementById('skill-direct-use-copy') as HTMLButtonElement | null;
  const directUseCopyStatus = document.getElementById('skill-direct-use-copy-status');
  const cloneCommand = document.getElementById('skill-git-clone-command');
  const cloneCopy = document.getElementById('skill-git-clone-copy') as HTMLButtonElement | null;
  const cloneCopyStatus = document.getElementById('skill-git-clone-copy-status');
  const digestCopy = document.getElementById('skill-digest-copy') as HTMLButtonElement | null;

  function updateInstallCommand(): void {
    if (!installTarget || !installCommand) return;
    installCommand.textContent = installCommandForTarget(skill, installTarget.value);
    if (installCopyStatus) installCopyStatus.textContent = '';
  }

  installTarget?.addEventListener('change', updateInstallCommand);
  installCopy?.addEventListener('click', async function() {
    await copyTextWithStatus(
      installCommand?.textContent || '',
      installCopyStatus,
      'Copied install command.',
      'Copy failed. Select and copy the command manually.',
    );
  });
  directUseCopy?.addEventListener('click', async function() {
    await copyTextWithStatus(
      directUseUrl?.textContent || '',
      directUseCopyStatus,
      'Copied SKILL.md URL.',
      'Copy failed. Select and copy the URL manually.',
    );
  });
  cloneCopy?.addEventListener('click', async function() {
    await copyTextWithStatus(
      cloneCommand?.textContent || '',
      cloneCopyStatus,
      'Copied clone command.',
      'Copy failed. Select and copy the command manually.',
    );
  });
  digestCopy?.addEventListener('click', async function() {
    await copyTextWithToast(fullSkillDigest(skill));
  });
}

export function renderSkillMarkdownContent(content?: string | null): void {
  const skillMdEl = document.getElementById('skill-md-content');
  if (!skillMdEl || !content) return;

  skillMdEl.innerHTML = renderSkillMd(content);
  skillMdEl.querySelectorAll<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6').forEach(function(heading) {
    const sourceLevel = Number(heading.tagName.slice(1));
    const targetLevel = Math.min(sourceLevel + 2, 6);
    const replacement = document.createElement(`h${targetLevel}`);
    for (const attribute of Array.from(heading.attributes)) {
      replacement.setAttribute(attribute.name, attribute.value);
    }
    replacement.append(...Array.from(heading.childNodes));
    heading.replaceWith(replacement);
  });
  skillMdEl.querySelectorAll('pre code').forEach(function(block, index) {
    const btn = document.createElement('button');
    btn.className = 'btn-ghost text-xs';
    btn.style.cssText = 'position:absolute;top:0.5rem;right:0.5rem';
    const firstLine = (block.textContent || '').trim().split('\n')[0]?.slice(0, 48);
    btn.setAttribute('aria-label', firstLine ? 'Copy code block: ' + firstLine : 'Copy code block ' + (index + 1));
    btn.textContent = 'Copy';
    btn.onclick = function() {
      navigator.clipboard.writeText(block.textContent || '');
      btn.textContent = 'Copied!';
      setTimeout(function() { btn.textContent = 'Copy'; }, 2000);
    };
    const pre = block.parentElement;
    if (pre) {
      pre.style.position = 'relative';
      pre.appendChild(btn);
    }
  });
}

function initFeedbackIssueActions(skillId: number): void {
  const analyticsEl = document.getElementById('skill-analytics-section');
  if (!analyticsEl) return;
  analyticsEl.addEventListener('click', async function(event) {
    const target = event.target as HTMLElement | null;
    const button = target?.closest('[data-skill-feedback-issue="true"]') as HTMLButtonElement | null;
    if (!button || button.disabled) return;
    const feedbackId = parseInt(button.dataset.feedbackId || '', 10);
    if (!Number.isInteger(feedbackId) || feedbackId <= 0) return;

    button.disabled = true;
    const originalText = button.innerHTML;
    button.textContent = 'Creating...';
    try {
      const result = await window.SkillDetailData.createIssueFromFeedback(skillId, feedbackId);
      if (result.ok) {
        button.outerHTML = '<a class="btn-ghost mt-2 inline-flex text-xs" href="' + escapeHtml(result.data.issue.url) + '">Issue #' + result.data.issue.id + '</a>';
        showToast('Issue created from feedback');
      } else {
        button.disabled = false;
        button.innerHTML = originalText;
        showToast(result.error || 'Issue creation failed', 'error');
      }
    } catch (error) {
      button.disabled = false;
      button.innerHTML = originalText;
      showToast((error as Error).message || 'Issue creation failed', 'error');
    }
  });
}

export function loadSkillAnalytics(skillId: number, feedback?: SkillFeedbackResponse | null): void {
  if (!window.SkillDetailData || !window.SkillAnalyticsRenderers) return;

  window.SkillDetailData.loadUsageStats(skillId).then(function(result: DataResult<UsageStatsResponse>) {
    const analyticsEl = document.getElementById('skill-analytics-section');
    if (analyticsEl && result.ok) {
      analyticsEl.innerHTML = window.SkillAnalyticsRenderers.renderAnalytics(
        result.data,
        feedback,
      );
      initFeedbackIssueActions(skillId);
    } else if (analyticsEl) {
      analyticsEl.innerHTML = '<div class="card p-5 text-zinc-500 text-sm">Analytics unavailable</div>';
    }
  }).catch(function(e: unknown) {
    console.warn('Failed to load analytics:', e);
  });
}
